// frontend/src/pages/HowItWorks.js
import React from 'react';
import { Link } from '../routing';
import { Shield, Clock, RotateCcw, Sparkles, KeyRound, CreditCard, MapPin, Phone, ExternalLink, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import PageHero from '../components/PageHero';
import { Reveal } from '../components/Motion';
import theme from '../theme';

// Same guard as on the product page: latitude and longitude arrive from
// Postgres as strings, and an unchecked pair would build a maps link pointing
// at 0,0 for any station whose coordinates were never filled in.
const mapsUrl = (m) => {
  const lat = Number(m?.latitude);
  const lng = Number(m?.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) return null;
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
};

// The cabinet is mounted outside on the street, so "any time, day or night" is
// literally true and is the strongest thing this page can say about collection
// — someone flying at 6am or coming home from an event at midnight can still
// get their extensions. Read from the record rather than hardcoded: a future
// cabinet placed indoors would run on shop hours, and the field is the only
// thing an operator can change without a deploy.
const alwaysOpen = (m) => /24\s*[\/x-]\s*7|24ω|24 ώρες/i.test(m?.opening_hours || '');

// The self-hosted copy in public/fonts, referenced once from index.html.
// This used to be a Google Fonts URL injected per page, which disclosed every
// visitor's IP to a third party on every page load and contradicted the privacy
// policy's "no third-party requests" claim. Kept as a constant because several
// pages import it into an @import rule rather than a <link>.
const FONTS = '/fonts/fonts.css';
const STEP_ICONS = [
  <Sparkles size={32} color={theme.gold} />,
  <CreditCard size={32} color={theme.gold} />,
  <KeyRound size={32} color={theme.gold} />,
  <MapPin size={32} color={theme.gold} />,
  <Clock size={32} color={theme.gold} />,
  <RotateCcw size={32} color={theme.gold} />,
];

export default function HowItWorks() {
  const { t } = useTranslation();
  const steps = t('howItWorks.steps', { returnObjects: true });
  const pricing = t('howItWorks.pricing', { returnObjects: true });

  // This page explained the whole process without ever saying where it happens.
  // The addresses, hours and phone numbers have existed in the lockers table all
  // along; nothing was showing them to anyone.
  const [machines, setMachines] = React.useState(null);
  const [failed, setFailed]     = React.useState(false);
  const [nonce, setNonce]       = React.useState(0);

  React.useEffect(() => {
    const link = document.createElement('link'); link.rel = 'stylesheet'; link.href = FONTS; document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    setFailed(false);
    api.get('/locker/machines')
      .then(list => { if (!cancelled) setMachines(Array.isArray(list) ? list : []); })
      // There is no fallback string on this page, so an outage has to say so and
      // offer a retry — silently rendering nothing would read as "GlamGo has no
      // stations", which is worse than admitting the request failed.
      .catch(() => { if (!cancelled) { setMachines([]); setFailed(true); } });
    return () => { cancelled = true; };
  }, [nonce]);

  const onePoint = (machines || []).length === 1;

  return (
    <div style={{ minHeight: '100vh', background: theme.cream, fontFamily: theme.font.body }}>
      {/* Hero */}
      <PageHero
        eyebrow={t('howItWorks.label')}
        title={t('howItWorks.title')}
        subtitle={t('howItWorks.subtitle')}
        height={360}
      />

      {/* Steps */}
      {/* `1fr` is `minmax(auto, 1fr)`, and that `auto` floor is the min-content
          width of the column — so a single unbreakable word sets the width of
          the grid. Step 4's Greek heading, «Παραλαμβάνεις από το Locker», needs
          259px at 32px and the track had 230, which is where the page's 5px of
          horizontal scroll on a 390px phone came from. At the time the heading
          was being drawn in the browser's fallback serif, because neither brand
          face carried Greek at all; it is EB Garamond now, and narrower. The
          floor is still wrong to leave in — Greek and Russian both run longer
          than English, and the next locale will find it again.

          minmax(0, 1fr) removes the floor so no translation can push the track
          out again, the narrow layout gives the text column the numeral's space
          back, and the heading may break a word rather than overflow. All three,
          because the first two keep the type intact and the third is the net. */}
      <style>{`
        @media (max-width: 560px) {
          .gg-step-row { grid-template-columns: 52px minmax(0, 1fr) !important; gap: 16px !important; }
          .gg-step-num { font-size: 40px !important; }
        }
      `}</style>
      <section style={{ padding: '80px 24px', maxWidth: 900, margin: '0 auto' }}>
        {steps.map((step, i) => (
          <Reveal key={i} y={26} className="gg-step-row" style={{ display: 'grid', gridTemplateColumns: '80px minmax(0, 1fr)', gap: 32, marginBottom: 64, alignItems: 'start' }}>
            <div style={{ textAlign: 'center' }}>
              <div className="gg-step-num" style={{ fontFamily: theme.font.display, fontSize: 64, color: theme.sand, lineHeight: 1, marginBottom: 8 }}>0{i + 1}</div>
              {STEP_ICONS[i]}
            </div>
            <div>
              <h2 style={{ fontFamily: theme.font.display, fontSize: 32, color: theme.ink, margin: '0 0 16px', fontWeight: 500, overflowWrap: 'break-word' }}>{step.title}</h2>
              <p style={{ color: theme.grey600, fontSize: 15, lineHeight: 1.8, margin: '0 0 16px' }}>{step.desc}</p>
              <div style={{ background: theme.creamDeep, borderLeft: `3px solid ${theme.gold}`, padding: '12px 16px', borderRadius: '0 4px 4px 0', color: theme.goldInk, fontSize: 13 }}>{step.tip}</div>
            </div>
          </Reveal>
        ))}
      </section>

      {/* Where it actually happens */}
      <section style={{ background: theme.white, padding: '72px 24px', borderTop: `1px solid ${theme.sand}` }}>
        <Reveal style={{ maxWidth: 700, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <MapPin size={22} color={theme.gold} />
            <h2 style={{ fontFamily: theme.font.display, fontSize: 36, color: theme.ink, margin: 0, fontWeight: 400 }}>
              {onePoint ? t('collectionPoint.titleOne') : t('collectionPoint.titleMany')}
            </h2>
          </div>
          <p style={{ color: theme.grey600, fontSize: 15, lineHeight: 1.8, margin: '0 0 28px' }}>
            {onePoint ? t('collectionPoint.subtitleOne') : t('collectionPoint.subtitleMany')}
          </p>

          {machines === null ? (
            <div style={{ color: theme.grey400, fontSize: 14 }}>{t('collectionPoint.loading')}</div>
          ) : machines.length === 0 ? (
            <div style={{ background: theme.cream, border: `1px solid ${theme.sand}`, borderRadius: 4, padding: 24 }}>
              <p style={{ color: theme.grey600, fontSize: 14, lineHeight: 1.7, margin: '0 0 14px' }}>
                {failed ? t('collectionPoint.loadFailed') : t('collectionPoint.none')}
              </p>
              {failed && (
                <button onClick={() => setNonce(n => n + 1)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'none', border: `1px solid ${theme.gold}`, color: theme.goldInk, padding: '11px 24px', borderRadius: 2, fontSize: 12, letterSpacing: '0.08em', cursor: 'pointer', fontFamily: theme.font.body }}>
                  <RefreshCw size={13} /> {t('collectionPoint.retry')}
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: onePoint ? '1fr' : 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: 16 }}>
              {machines.map(m => (
                <div key={m.id} style={{ background: theme.cream, border: `1px solid ${theme.sand}`, borderRadius: 4, padding: 24 }}>
                  <div style={{ fontFamily: theme.font.display, fontSize: 24, color: theme.ink, marginBottom: 6 }}>{m.name}</div>
                  {m.address && <div style={{ color: theme.grey600, fontSize: 14, lineHeight: 1.7 }}>{m.address}</div>}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: alwaysOpen(m) ? theme.green : theme.goldInk, fontSize: 13, marginTop: 12 }}>
                    <Clock size={14} color={alwaysOpen(m) ? theme.green : theme.gold} />
                    <span>{alwaysOpen(m) ? t('collectionPoint.alwaysOpen') : (m.opening_hours || t('collectionPoint.hoursUnknown'))}</span>
                  </div>
                  {m.phone && (
                    <a href={`tel:${m.phone}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: theme.goldInk, fontSize: 13, textDecoration: 'none', marginTop: 10 }}>
                      <Phone size={14} color={theme.gold} /> {m.phone}
                    </a>
                  )}
                  {/* Linked out rather than embedded: on the Android build this
                      URL opens the phone's own maps app with directions already
                      loaded, which is what someone reading this page next wants. */}
                  {mapsUrl(m) && (
                    <div>
                      <a href={mapsUrl(m)} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 16, background: 'none', border: `1px solid ${theme.gold}`, color: theme.goldInk, padding: '11px 22px', borderRadius: 2, fontSize: 12, letterSpacing: '0.08em', textDecoration: 'none' }}>
                        <ExternalLink size={13} /> {t('collectionPoint.mapLink')}
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Reveal>
      </section>

      {/* Pricing table */}
      <section style={{ background: theme.white, padding: '80px 24px' }}>
        <Reveal style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontFamily: theme.font.display, fontSize: 48, color: theme.ink, marginBottom: 48 }}>{t('howItWorks.pricingTitle')}</h2>
          {/* Two columns need room the phone does not have: even before the
              deposit row gained its condition, "€5/day past the due date" was
              breaking across four lines against a label pinned to the far edge.
              Under 560px the label sits above its value and both read left. */}
          <style>{`
            @media (max-width: 560px) {
              .gg-price-row { flex-direction: column; align-items: flex-start !important; gap: 4px !important; }
              .gg-price-row > span:last-child { text-align: left !important; }
            }
          `}</style>
          <div style={{ background: theme.cream, border: `1px solid ${theme.sand}`, borderRadius: 4, overflow: 'hidden' }}>
            {/* The deposit row carries the condition under which it is returned,
                so a value in this column is a sentence rather than a figure.
                space-between alone would squash the label against it and let it
                wrap mid-word: the label keeps its intrinsic width, the value
                takes the rest and stays right-aligned. */}
            {pricing.map(([item, price], i) => (
              <div key={item} className="gg-price-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: theme.space.lg, padding: '18px 28px', borderBottom: i < pricing.length - 1 ? `1px solid ${theme.sand}` : 'none', background: i % 2 === 0 ? theme.cream : theme.white, textAlign: 'left' }}>
                <span style={{ color: theme.grey600, fontSize: 15, flexShrink: 0 }}>{item}</span>
                <span style={{ fontFamily: theme.font.display, fontSize: 18, color: theme.ink, textAlign: 'right', lineHeight: theme.leading.snug }}>{price}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* CTA */}
      <section style={{ padding: '80px 24px', textAlign: 'center', background: theme.espresso }}>
        <Reveal>
        <h2 style={{ fontFamily: theme.font.display, fontSize: 48, color: theme.white, fontWeight: 300, marginBottom: 24 }}>{t('howItWorks.ctaTitle')}</h2>
        <Link to="/browse" style={{ display: 'inline-block', background: `linear-gradient(135deg, ${theme.gold}, ${theme.goldDeep})`, color: theme.white, padding: '18px 56px', borderRadius: 2, textDecoration: 'none', fontSize: 13, letterSpacing: '0.15em' }}>
          {t('howItWorks.ctaBtn')}
        </Link>
        </Reveal>
      </section>
    </div>
  );
}
